import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../lib/auth-context';
import { fetchJobById, fetchJobsOffers } from '../lib/api';
import { Job } from '../types/jobs';
import { ArrowLeft, Building2, MapPin, DollarSign, Clock, Calendar, CheckCircle, Globe, Award, ExternalLink, Briefcase, MailCheck } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { SEO } from '../components/SEO';
import { openWebRedirect } from '../lib/api';
import * as amplitude from '@amplitude/analytics-browser';

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user, userInfo } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = React.useState<Job | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [relatedJobs, setRelatedJobs] = React.useState<Job[]>([]);
  const hasTrackedRef = React.useRef(false);

  // Redirect to home if not authenticated
  React.useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  React.useEffect(() => {
    if (!hasTrackedRef.current && job) {
      // Analytics tracking for job detail view
      amplitude.track("Job Detail View", {
        jobId: job.id,
        jobTitle: job.title || 'Unknown job',
        featured: job.featured || false
      });
      hasTrackedRef.current = true;
    }
  }, [job]);

  // Function to safely decode HTML and convert BBCode
  const decodeHTML = (html?: string) => {
    if (!html) return '';
    try {
      // First decode HTML entities
      const txt = document.createElement('textarea');
      txt.innerHTML = html;
      let content = txt.value;

      // Then convert BBCode to HTML
      // Replace [COLOR=value]text[/COLOR]
      content = content.replace(/\[COLOR=([^\]]+)\]([\s\S]*?)\[\/COLOR\]/g, (match, color, text) => {
        // Handle variables in color values like var(--tw-prose-bold)
        if (color.includes('--tw-prose-bold')) {
          return `<strong>${text}</strong>`;
        }
        // Other color values
        return `<span style="color: ${color};">${text}</span>`;
      });

      // Replace [B] or [BOLD]
      content = content.replace(/\[B\]([\s\S]*?)\[\/B\]/gi, '<strong>$1</strong>');
      content = content.replace(/\[BOLD\]([\s\S]*?)\[\/BOLD\]/gi, '<strong>$1</strong>');

      // Replace [I] or [ITALIC]
      content = content.replace(/\[I\]([\s\S]*?)\[\/I\]/gi, '<em>$1</em>');
      content = content.replace(/\[ITALIC\]([\s\S]*?)\[\/ITALIC\]/gi, '<em>$1</em>');

      // Replace [U] or [UNDERLINE]
      content = content.replace(/\[U\]([\s\S]*?)\[\/U\]/gi, '<u>$1</u>');
      content = content.replace(/\[UNDERLINE\]([\s\S]*?)\[\/UNDERLINE\]/gi, '<u>$1</u>');
      
      // Replace [SIZE=value]text[/SIZE]
      content = content.replace(/\[SIZE=([^\]]+)\]([\s\S]*?)\[\/SIZE\]/gi, (match, size, text) => {
        // Convert size to pixels or relative unit
        return `<span style="font-size: ${size};">${text}</span>`;
      });
      
      // Replace [URL=url]text[/URL]
      content = content.replace(/\[URL=([^\]]+)\]([\s\S]*?)\[\/URL\]/gi, '<a href="$1" target="_blank" rel="noopener noreferrer">$2</a>');
      
      // Replace [URL]url[/URL]
      content = content.replace(/\[URL\]([\s\S]*?)\[\/URL\]/gi, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');

      return content;
    } catch (e) {
      console.error('HTML decode error:', e);
      return html || '';
    }
  };
  
  // Format publication date with improved error handling
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Non spécifiée';
    
    try {
      // First check if the string is a valid date format
      const parsedDate = parseISO(dateString);
      if (!isValid(parsedDate)) {
        return 'Non spécifiée';
      }
      return format(parsedDate, 'd MMMM yyyy', { locale: fr });
    } catch (e) {
      console.error('Date formatting error:', e);
      return 'Non spécifiée';
    }
  };

  // Get employer display name
  const getEmployerDisplay = (employer: any): string => {
    if (typeof employer === 'object' && employer !== null && !Array.isArray(employer)) {
      // Handle the case where employer is an object with a name property
      return employer.name || "Employeur non spécifié";
    } else if (Array.isArray(employer) && employer.length > 0) {
      // Handle the case where employer is an array with content
      if (typeof employer[0] === 'string') {
        return employer[0];
      } else if (typeof employer[0] === 'object' && employer[0]?.name) {
        return employer[0].name;
      }
    } else if (typeof employer === 'string' && employer) {
      // Handle the case where employer is a string
      return employer;
    }
    
    // Fallback for empty arrays or null/undefined employers
    return "Employeur non spécifié";
  };

  React.useEffect(() => {
    const fetchJob = async () => {
      if (!id) return;

      setIsLoading(true);
      setError(null);

      try {
        const jobData = await fetchJobById(id, userInfo);
        setJob(jobData);
        
        // Fetch related jobs (could be from same category or employer)
        try {
          const relatedJobsData = await fetchJobsOffers(`/jobs?limit=3&category=${jobData.category || 0}`, userInfo);
          if (Array.isArray(relatedJobsData)) {
            // Filter out the current job
            setRelatedJobs(relatedJobsData.filter(j => j.id !== id));
          } else if (relatedJobsData.data && Array.isArray(relatedJobsData.data)) {
            setRelatedJobs(relatedJobsData.data.filter(j => j.id !== id));
          }
        } catch (error) {
          console.error('Error fetching related jobs:', error);
        }
      } catch (error) {
        console.error('Error fetching job:', error);
        setError('Impossible de charger cette offre d\'emploi');
      } finally {
        setIsLoading(false);
      }
    };

    if (user && id) {
      fetchJob();
    }
  }, [id, user, userInfo]);

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <SEO 
        title={job ? `${decodeHTML(job.title)} - Offre d'emploi PVT` : "Détail d'offre d'emploi - pvtistes.net"}
        description={job ? `Détails de l'offre d'emploi "${decodeHTML(job.title)}"${job.location ? ` à ${job.location}` : ''}. Postulez maintenant!` : "Consultez cette offre d'emploi pour Pvtistes"}
        image="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?q=80&w=2574&auto=format&fit=crop"
      />
      
      <Navbar 
        onLogin={() => {}} 
        onAdminClick={() => {}}
        onHomeClick={() => {}}
        isAdminMode={false}
      />

      <div className="fixed top-16 left-0 right-0 z-40 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border-b border-gray-200/50 dark:border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <button
              onClick={() => navigate('/jobs')}
              className="flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Retour aux offres</span>
            </button>
          </div>
        </div>
      </div>

      <main className="flex-1 pt-28 sm:pt-32 pb-24 sm:pb-12">
        {isLoading ? (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <div className="animate-pulse space-y-6 sm:space-y-8">
              {/* Job title skeleton */}
              <div className="h-7 sm:h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-3/4 max-w-lg" />
              
              {/* Company info skeleton */}
              <div className="h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/2 max-w-xs" />
              
              {/* Job details skeleton */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-24 sm:h-28 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                ))}
              </div>
              
              {/* Description skeleton */}
              <div className="space-y-3 sm:space-y-4 mt-6">
                <div className="h-6 sm:h-7 bg-gray-200 dark:bg-gray-700 rounded-lg w-48" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/6" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 sm:p-8 text-center">
              <h2 className="text-xl font-semibold text-red-900 dark:text-red-300 mb-4">
                {error}
              </h2>
              <p className="text-red-600 dark:text-red-400 mb-6">
                Cette offre n'est peut-être plus disponible ou a été supprimée.
              </p>
              <div className="mt-2">
                <button
                  onClick={() => navigate('/jobs')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                >
                  <span>Retour aux offres d'emploi</span>
                  <ArrowLeft className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ) : job ? (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            {/* Premium/Featured banner - Mobile optimized */}
            {(job.featured || job.employerVerified) && (
              <div className={`w-full py-3 px-4 flex items-center justify-center gap-2.5 text-sm font-semibold mb-4 rounded-2xl ${
                job.featured
                  ? 'bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 text-white shadow-lg shadow-amber-200 dark:shadow-amber-900/30'
                  : 'bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-white shadow-lg shadow-emerald-200 dark:shadow-emerald-900/30'
              }`}>
                {job.featured ? (
                  <>
                    <Award className="w-5 h-5" />
                    <span className="text-base">Offre Premium</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-base">Employeur vérifié</span>
                  </>
                )}
              </div>
            )}

            {/* Job header card - Mobile first design */}
            <div className="bg-gradient-to-br from-white via-gray-50 to-blue-50/30 dark:from-gray-800 dark:via-gray-800 dark:to-blue-900/10 rounded-3xl shadow-xl border-2 border-gray-100 dark:border-gray-700/50 overflow-hidden mb-6">
              {/* Job title and company */}
              <div className="p-6 space-y-5">
                <h1 className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-blue-900 to-cyan-900 dark:from-white dark:via-blue-200 dark:to-cyan-200 leading-tight">
                  {decodeHTML(job.title)}
                </h1>

                {getEmployerDisplay(job.employer) !== "Employeur non spécifié" && (
                  <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-900/50 rounded-2xl border border-gray-200 dark:border-gray-700">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl shadow-lg">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                      {getEmployerDisplay(job.employer)}
                    </span>
                  </div>
                )}

                {job.applyUrl && (
                  <a
                    href={job.applyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      amplitude.track('Job Apply From Detail', {
                        jobId: job.id,
                        jobTitle: job.title || 'Unknown job',
                        featured: job.featured || false
                      });
                      openWebRedirect(job.applyUrl, userInfo);
                    }}
                    className={`w-full flex items-center justify-center gap-3 px-8 py-5 text-white text-lg font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] ${
                      job.featured
                        ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:from-amber-600 hover:via-orange-600 hover:to-red-600'
                        : 'bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 hover:from-blue-700 hover:via-cyan-700 hover:to-teal-700'
                    }`}
                  >
                    <ExternalLink className="w-6 h-6" />
                    <span>Postuler maintenant</span>
                  </a>
                )}
              </div>
            </div>

            {/* Key details grid - Mobile optimized with vibrant design */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="group bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-bold text-white/90 uppercase tracking-wider">Localisation</span>
                </div>
                <div className="text-xl font-black text-white">
                  {job.location || 'Non spécifié'}
                  {job.locationFlexible === 1 && (
                    <span className="ml-2 block mt-2 text-base text-white/90 font-semibold inline-flex items-center gap-1.5">
                      <Globe className="w-4 h-4" />
                      Flexible
                    </span>
                  )}
                </div>
              </div>

              <div className="group bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-bold text-white/90 uppercase tracking-wider">Salaire</span>
                </div>
                <div className="text-xl font-black text-white">
                  {job.salaryFull ||
                   (job.salary && (
                     `${job.salary} ${job.salaryCurrency || ''}${job.salaryPeriod ? ` ${job.salaryPeriod === 'hourly' ? 'par heure' :
                     job.salaryPeriod === 'weekly' ? 'par semaine' :
                     job.salaryPeriod === 'monthly' ? 'par mois' :
                     job.salaryPeriod === 'yearly' ? 'par an' :
                     job.salaryPeriod}` : ''}`
                   )) || 'À négocier'}
                </div>
              </div>

              <div className="group bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-bold text-white/90 uppercase tracking-wider">Date de début</span>
                </div>
                <div className="text-xl font-black text-white">
                  {job.startASAP ? 'Dès que possible' :
                   (job.startDate ? formatDate(job.startDate) : 'Non spécifiée')}
                </div>
              </div>

              <div className="group bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-bold text-white/90 uppercase tracking-wider">Publication</span>
                </div>
                <div className="text-xl font-black text-white">
                  {formatDate(job.published)}
                </div>
              </div>
            </div>

            {/* Job description card - Mobile optimized */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl border-2 border-gray-100 dark:border-gray-700/50 overflow-hidden mb-6">
              <div className="bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 p-5">
                <h2 className="text-2xl font-black text-white flex items-center gap-3">
                  <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl">
                    <Briefcase className="w-6 h-6 text-white" />
                  </div>
                  <span>Description du poste</span>
                </h2>
              </div>

              <div className="p-6 sm:p-8">

                <div
                  className="prose prose-base sm:prose-lg dark:prose-invert max-w-none
                            prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white prose-headings:mb-4 prose-headings:mt-6
                            prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
                            prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-semibold
                            prose-ul:my-4 prose-ul:space-y-2
                            prose-li:text-gray-700 dark:prose-li:text-gray-300 prose-li:leading-relaxed
                            prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
                            [&>*:first-child]:mt-0"
                  dangerouslySetInnerHTML={{
                    __html: decodeHTML(job.description) || '<p class="text-gray-500 dark:text-gray-400">Aucune description disponible</p>'
                  }}
                />

                {/* Apply button - bottom - Mobile optimized */}
                {job.applyUrl && (
                  <div className="mt-8">
                    <div className="bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 dark:from-blue-900/20 dark:via-cyan-900/20 dark:to-teal-900/20 rounded-2xl p-6 border-2 border-blue-200 dark:border-blue-700">
                      <div className="text-center space-y-4">
                        <div className="flex justify-center">
                          <div className="p-4 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl shadow-lg">
                            <ExternalLink className="w-8 h-8 text-white" />
                          </div>
                        </div>
                        <div>
                          <p className="text-xl font-black text-gray-900 dark:text-white mb-2">
                            Cette offre vous intéresse ?
                          </p>
                          <p className="text-base text-gray-700 dark:text-gray-300 font-medium">
                            Postulez directement en un clic
                          </p>
                        </div>
                        <a
                          href={job.applyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            amplitude.track('Job Apply From Detail', {
                              jobId: job.id,
                              jobTitle: job.title || 'Unknown job',
                              featured: job.featured || false
                            });
                            openWebRedirect(job.applyUrl, userInfo);
                          }}
                          className={`w-full flex items-center justify-center gap-3 px-8 py-5 text-white text-lg font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] ${
                            job.featured
                              ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:from-amber-600 hover:via-orange-600 hover:to-red-600'
                              : 'bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 hover:from-blue-700 hover:via-cyan-700 hover:to-teal-700'
                          }`}
                        >
                          <ExternalLink className="w-6 h-6" />
                          <span>Postuler maintenant</span>
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Job alerts CTA - Mobile optimized */}
            <div className="bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-600 rounded-3xl shadow-2xl overflow-hidden mb-6">
              <div className="p-6 sm:p-8 text-center space-y-5">
                <div className="flex justify-center">
                  <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
                    <MailCheck className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="space-y-3">
                  <h3 className="text-2xl font-black text-white">
                    Ne manquez aucune opportunité
                  </h3>
                  <p className="text-white/90 text-base font-medium">
                    Recevez les nouvelles offres d'emploi directement dans votre boîte mail
                  </p>
                </div>
                <a
                  href="https://pvtistes.net/jobs/index.php?do=notifications"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-3 px-8 py-5 bg-white hover:bg-gray-50 text-blue-600 text-lg font-black rounded-2xl transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-[1.02]"
                  onClick={(e) => {
                    e.preventDefault();
                    amplitude.track("Job Notifications Click");
                    openWebRedirect("https://pvtistes.net/jobs/index.php?do=notifications", userInfo);
                  }}
                >
                  <span>S'inscrire aux alertes</span>
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Related jobs */}
            {relatedJobs.length > 0 && (
              <div className="mb-10 sm:mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Offres similaires
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatedJobs.slice(0, 3).map(relatedJob => (
                    <div
                      key={relatedJob.id}
                      className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 p-5 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-200 dark:hover:border-gray-600/50"
                      onClick={() => navigate(`/jobs/${relatedJob.id}`)}
                    >
                      <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {decodeHTML(relatedJob.title)}
                      </h3>

                      {getEmployerDisplay(relatedJob.employer) !== "Employeur non spécifié" && (
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
                          <Building2 className="w-4 h-4 flex-shrink-0" />
                          <span className="text-sm font-medium truncate">{getEmployerDisplay(relatedJob.employer)}</span>
                        </div>
                      )}

                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-4">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm truncate">{relatedJob.location || 'Lieu non spécifié'}</span>
                      </div>

                      <div className="pt-4 border-t border-gray-100 dark:border-gray-700/50 flex justify-between items-center">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(relatedJob.published)}
                        </span>
                        <div className="text-blue-600 dark:text-blue-400 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                          <span>Voir</span>
                          <ArrowLeft className="w-3.5 h-3.5 transform rotate-180" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 sm:p-8 text-center">
              <h2 className="text-xl font-semibold text-red-900 dark:text-red-300 mb-4">
                Offre non trouvée
              </h2>
              <p className="text-red-600 dark:text-red-400 mb-6">
                Cette offre n'est peut-être plus disponible ou a été supprimée.
              </p>
              <div className="mt-2">
                <button
                  onClick={() => navigate('/jobs')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                >
                  <span>Retour aux offres d'emploi</span>
                  <ArrowLeft className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}